import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { parentService } from '../../services/api';

const schema = yup.object({
  russian_name: yup.string().required('Nombre en ruso es requerido'),
  spanish_name: yup.string().nullable(),
  email: yup.string().email('Email inválido').nullable(),
  phone: yup.string().nullable(),
  country_code: yup.string().required('Código de país es requerido'),
  is_active: yup.boolean().default(true),
  notes: yup.string().nullable()
});

const countryCodes = [
  { value: '+7', label: 'Rusia (+7)', flag: '🇷🇺' },
  { value: '+53', label: 'Cuba (+53)', flag: '🇨🇺' },
  { value: '+1', label: 'USA/Canadá (+1)', flag: '🇺🇸' },
  { value: '+34', label: 'España (+34)', flag: '🇪🇸' },
  { value: '+49', label: 'Alemania (+49)', flag: '🇩🇪' },
  { value: '+33', label: 'Francia (+33)', flag: '🇫🇷' },
  { value: '+39', label: 'Italia (+39)', flag: '🇮🇹' },
  { value: '+44', label: 'Reino Unido (+44)', flag: '🇬🇧' }
];

const ParentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      russian_name: '',
      spanish_name: '',
      email: '',
      phone: '',
      country_code: '+7',
      is_active: true,
      notes: ''
    }
  });

  const watchRussianName = watch('russian_name');
  const watchSpanishName = watch('spanish_name');

  useEffect(() => {
    if (isEdit) {
      loadParent();
    }
  }, [id]);

  const loadParent = async () => {
    try {
      setLoading(true);
      const response = await parentService.getById(parseInt(id!));
      reset(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar padre');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      if (isEdit) {
        await parentService.update(parseInt(id!), data);
      } else {
        await parentService.create(data);
      }

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/parents');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar padre');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/parents" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Padre/Tutor' : 'Nuevo Padre/Tutor'}
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Padre/Tutor guardado exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Información Principal */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información Principal
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="russian_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Nombre en Ruso *"
                          placeholder="Ej: Иванов Иван Иванович"
                          error={!!errors.russian_name}
                          helperText={errors.russian_name?.message}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LanguageIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="spanish_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Nombre en Español"
                          placeholder="Ej: Juan Pérez"
                          error={!!errors.spanish_name}
                          helperText={errors.spanish_name?.message}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {(watchRussianName || watchSpanishName) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Nombre completo:</strong>{' '}
                      {watchSpanishName || watchRussianName}
                      {watchSpanishName && watchRussianName && (
                        <> (Ruso: {watchRussianName})</>
                      )}
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Información de Contacto */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información de Contacto
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Email"
                          type="email"
                          placeholder="ejemplo@correo.com"
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="country_code"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          label="Código País"
                          error={!!errors.country_code}
                          helperText={errors.country_code?.message}
                          disabled={loading}
                        >
                          {countryCodes.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box display="flex" alignItems="center">
                                <span style={{ marginRight: 8 }}>{option.flag}</span>
                                {option.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Teléfono"
                          placeholder="5551234567"
                          error={!!errors.phone}
                          helperText={errors.phone?.message}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Estado y Notas */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom>
                      Estado
                    </Typography>
                    
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              disabled={loading}
                              color="success"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1">
                                {field.value ? 'Activo' : 'Inactivo'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {field.value 
                                  ? 'El padre puede registrar estudiantes y realizar pagos'
                                  : 'El padre no puede realizar acciones en el sistema'
                                }
                              </Typography>
                            </Box>
                          }
                          labelPlacement="end"
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom>
                      Notas
                    </Typography>
                    
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Información adicional sobre el padre/tutor..."
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                <InfoIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Botones */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => navigate('/parents')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || success}
              >
                {loading ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ParentForm;